<template>
  <v-dialog
    v-model="openLoginModal"
    persistent
    max-width="520px"
    class="otp-login-overlay"
  >
    <v-card class="otp-login-card" :style="themeStyle">
      <div class="otp-login-card__hero">
        <div class="otp-login-card__logo">
          <Logo />
        </div>
        <span class="otp-login-card__eyebrow">Pre-approved booking access</span>
        <h1 class="otp-login-card__title">Sign in with OTP</h1>
        <p v-if="venueType" class="otp-login-card__venue">{{ venueType }}</p>
        <p class="otp-login-card__subtitle">
          {{
            step === 1
              ? "Enter your registered email to receive a one-time code."
              : "Enter the 6-digit code sent to your email and phone."
          }}
        </p>
      </div>

      <v-card-text class="otp-login-card__body">
        <v-form id="loginForm" @submit.prevent="handleFormSubmit">
          <div v-if="step === 1">
            <div class="otp-login-card__field">
              <label class="otp-login-card__label" for="otp-user-email">Email address</label>
              <v-text-field
                id="otp-user-email"
                v-model="userEmail"
                placeholder="you@example.com"
                variant="outlined"
                density="comfortable"
                hide-details
                :disabled="isLoading"
              />
            </div>

            <div v-if="pendingBookingHours != null" class="otp-login-card__quota">
              <span class="otp-login-card__quota-label">Hours available</span>
              <span class="otp-login-card__quota-value">{{ pendingBookingHours }} hrs</span>
            </div>

            <p v-if="errorMessage" class="otp-login-card__error">{{ errorMessage }}</p>

            <div v-if="isLoading" class="otp-login-card__loading">
              <v-progress-circular
                indeterminate
                :color="buttonColor"
                size="36"
              />
              <span>Checking your access…</span>
            </div>

            <div class="otp-login-card__actions">
              <button
                type="button"
                class="otp-login-card__btn"
                :disabled="isLoading"
                @click="goToOTP"
              >
                {{ isLoading ? "Please wait…" : "Send OTP" }}
              </button>
              <button
                type="button"
                class="otp-login-card__btn-secondary"
                :disabled="isLoading"
                @click="redirectTohomepage"
              >
                Back to home
              </button>
            </div>
          </div>

          <div v-else>
            <div class="otp-login-card__field otp-login-card__otp">
              <label class="otp-login-card__label" for="otp-code-input">One-time code</label>
              <v-otp-input
                id="otp-code-input"
                v-model="otpCode"
                variant="outlined"
                length="6"
                :disabled="isLoading"
              />
            </div>

            <p class="otp-login-card__hint">
              Check your email and mobile for the 6-digit code. Codes expire after 24 hours.
            </p>
            <p v-if="errorMessage" class="otp-login-card__error">{{ errorMessage }}</p>

            <div v-if="isLoading" class="otp-login-card__loading">
              <v-progress-circular
                indeterminate
                :color="buttonColor"
                size="36"
              />
              <span>Verifying your code…</span>
            </div>

            <div class="otp-login-card__actions">
              <button
                type="submit"
                class="otp-login-card__btn"
                :disabled="isLoading || otpCode.length < 6"
              >
                Verify & continue
              </button>
              <button
                type="button"
                class="otp-login-card__btn-secondary"
                :disabled="isLoading"
                @click="redirectTohomepage"
              >
                Back to home
              </button>
            </div>

            <p class="otp-login-card__resend">
              Didn't receive a code?
              <button
                type="button"
                :disabled="isLoading || resendCooldown > 0"
                @click="resendOtp"
              >
                {{
                  resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend code"
                }}
              </button>
            </p>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { toastError, toastSuccess } from "@/toastNotification";
import {
  checkExistingOnetimeUser,
  onetimeLogin,
  sendOTPToEmail,
} from "../../../../server/config";
import Logo from "../../../components/LogoFCC.vue";
import * as ENV from "@/config.js";
import { isEventsVenueVenueType } from "@/utils/eventsVenueMatch.js";
import { isEventsBookingFacility } from "@/constants/eventsFacilityPromo.js";
import { applyBookingTheme, getContrastTextColor } from "@/utils/bookingTheme.js";
import { getBookingGeneral } from "@/store/vuexStore";
import {
  persistOtpSessionFromLookupAndLogin,
} from "@/utils/otpSession.js";
import "./onetimelogin.css";

const FREE_SESSION_VENUE = "BOOK A FREE SESSION";

export default {
  components: { Logo },
  name: "LoginPage",
  data() {
    return {
      step: 1,
      openLoginModal: false,
      userEmail: "",
      otpCode: "",
      venueType: sessionStorage.getItem("selectedvenuetype") || "",
      isLoading: false,
      errorMessage: "",
      lookupProfile: null,
      pendingBookingHours: null,
      resendCooldown: 0,
      resendTimer: null,
      buttonColor: "#0275ff",
      brandColor: "#00666c",
    };
  },
  computed: {
    themeStyle() {
      return {
        "--booking-button-color": this.buttonColor,
        "--booking-brand-color": this.brandColor,
        "--booking-button-font-color": getContrastTextColor(this.buttonColor),
      };
    },
  },
  methods: {
    async goToOTP() {
      this.errorMessage = "";
      const email = this.userEmail.trim();

      if (!email) {
        toastError("Please enter a valid email");
        return;
      }

      this.isLoading = true;
      try {
        const checkExistingUser = await checkExistingOnetimeUser(
          email,
          this.venueType
        );

        if (!checkExistingUser || checkExistingUser.status !== 1) {
          this.errorMessage =
            checkExistingUser?.message ||
            "You are not registered for this venue.";
          return;
        }

        if (!checkExistingUser.response?.email) {
          this.errorMessage = "Unable to verify email. Please try again.";
          return;
        }

        this.lookupProfile = checkExistingUser.response;
        this.pendingBookingHours = Number(checkExistingUser.response.bookinghours);
        if (Number.isNaN(this.pendingBookingHours)) {
          this.pendingBookingHours = null;
        }

        const otpResponse = await sendOTPToEmail(
          email,
          this.venueType,
          checkExistingUser.response.phone || ""
        );

        if (!otpResponse || otpResponse.status !== 1) {
          this.errorMessage =
            otpResponse?.message || "Unable to send OTP. Please try again.";
          return;
        }

        toastSuccess("A one-time code has been sent to your email and phone.");
        this.step = 2;
        this.startResendCooldown();
      } catch (error) {
        this.errorMessage = "An error occurred. Please try again.";
        console.error("Error in goToOTP:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async resendOtp() {
      if (this.resendCooldown > 0 || !this.userEmail || !this.lookupProfile) {
        return;
      }
      this.errorMessage = "";
      this.isLoading = true;
      try {
        const otpResponse = await sendOTPToEmail(
          this.userEmail.trim(),
          this.venueType,
          this.lookupProfile.phone || ""
        );
        if (!otpResponse || otpResponse.status !== 1) {
          this.errorMessage =
            otpResponse?.message || "Unable to resend OTP. Please try again.";
          return;
        }
        toastSuccess("A new code has been sent.");
        this.startResendCooldown();
      } finally {
        this.isLoading = false;
      }
    },
    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown <= 1) {
          this.resendCooldown = 0;
          clearInterval(this.resendTimer);
          this.resendTimer = null;
          return;
        }
        this.resendCooldown -= 1;
      }, 1000);
    },
    redirectTohomepage() {
      this.$router.push({ path: `/bookingapp/${ENV.VUE_APP_FACILITY}/` });
    },
    handleFormSubmit() {
      if (this.step === 1) {
        this.goToOTP();
      } else {
        this.submit();
      }
    },
    routeAfterLogin() {
      const newVenueType = this.venueType.replaceAll(" ", "_");
      if (this.venueType === FREE_SESSION_VENUE) {
        this.$router.push({
          path: "/bookingapp/endeavourclubs/freebooking",
          query: { venueType: newVenueType },
        });
        return;
      }

      if (isEventsBookingFacility() && isEventsVenueVenueType(this.venueType)) {
        this.$router.push({
          path: `/bookingapp/${ENV.VUE_APP_FACILITY}/eventsbooking`,
          query: { venueType: newVenueType },
        });
        return;
      }

      this.$router.push({
        path: `/bookingapp/${ENV.VUE_APP_FACILITY}/booking`,
        query: { venueType: newVenueType },
      });
    },
    async submit() {
      this.errorMessage = "";
      const email = this.userEmail.trim();
      const code = String(this.otpCode || "").trim();

      if (!email || code.length < 6) {
        this.errorMessage = "Please enter your email and 6-digit code.";
        return;
      }

      this.isLoading = true;
      try {
        const response = await onetimeLogin(email, code, this.venueType);
        if (!response || response.status !== 1 || !response.response?.token) {
          this.errorMessage =
            response?.message || "Email or OTP is invalid. Please try again.";
          return;
        }

        persistOtpSessionFromLookupAndLogin({
          facility: ENV.VUE_APP_FACILITY,
          venueType: this.venueType,
          lookupResponse: this.lookupProfile,
          loginResponse: response.response,
        });

        toastSuccess("Signed in successfully.");
        this.routeAfterLogin();
      } catch (error) {
        this.errorMessage = "Unable to complete login. Please try again.";
        console.error("OTP login failed:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  created() {
    const bookingGeneral = getBookingGeneral() || {};
    applyBookingTheme(bookingGeneral);
    this.buttonColor = bookingGeneral.buttoncolor || this.buttonColor;
    this.brandColor = bookingGeneral.color || this.brandColor;

    const queryVenueType = this.$route.query.venueType?.replaceAll("_", " ");
    if (queryVenueType) {
      this.venueType = queryVenueType;
      sessionStorage.setItem("selectedvenuetype", queryVenueType);
    } else if (!this.venueType) {
      this.venueType = sessionStorage.getItem("selectedvenuetype") || "";
    }

    this.openLoginModal = sessionStorage.getItem("loginmodal") !== "false";
  },
  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  },
};
</script>
